const NOSTO_LOG = false;
/**
 * Waits for the Nosto API to be fully loaded and ready to use
 * @param maxWaitTime Maximum time to wait in milliseconds (default: 15000)
 * @returns Promise that resolves to a ready NostoAPI instance or null if timeout
 */ const waitForNostoAPI = async (maxWaitTime = 15000)=>{
    if (typeof window === 'undefined') {
        console.warn('Nosto: Window is not available (SSR context)');
        return null;
    }
    if (NOSTO_LOG) {
        console.log('Nosto: Waiting for API to be ready...', {
            nosto: !!window.Nosto,
            nostojs: typeof window.nostojs === 'function'
        });
    }
    // Check if Nosto script exists in DOM
    const nostoScript = document.querySelector('script[src*="connect.nosto.com"]');
    if (!nostoScript && !window.Nosto) {
        console.warn('Nosto: No Nosto script found in DOM. Nosto may not be configured.');
        return null;
    }
    const startTime = Date.now();
    // Poll for Nosto libraries AND test API initialization
    while(Date.now() - startTime < maxWaitTime){
        // First check if libraries exist
        if (window.Nosto && typeof window.nostojs === 'function') {
            try {
                // Test if we can actually create the API instance
                const testApiPromise = new Promise(window.nostojs);
                const timeoutPromise = new Promise((_, reject)=>{
                    setTimeout(()=>reject(new Error('API test timeout')), 2000);
                });
                const api = await Promise.race([
                    testApiPromise,
                    timeoutPromise
                ]);
                // Verify the API has the methods we need
                if (api && typeof api.listen === 'function' && typeof api.loadRecommendations === 'function') {
                    if (NOSTO_LOG) {
                        console.log('Nosto: API ready and functional');
                    }
                    return api;
                } else {
                    if (NOSTO_LOG) {
                        console.log('Nosto: API initialized but methods not available yet, retrying...');
                    }
                }
            } catch (error) {
                // API not ready yet, continue polling
                if (NOSTO_LOG) {
                    console.log('Nosto: API not ready yet, retrying...', error instanceof Error ? error.message : 'Unknown error');
                }
            }
        } else {
            if (NOSTO_LOG) {
                console.log('Nosto: Libraries not loaded yet, waiting...');
            }
        }
        // Wait 200ms between checks (slightly longer since we're doing more work)
        await new Promise((resolve)=>setTimeout(resolve, 200));
    }
    console.warn('Nosto: Timeout waiting for API to be ready', {
        waitTime: maxWaitTime,
        nosto: !!window.Nosto,
        nostojs: typeof window.nostojs === 'function'
    });
    return null;
};
/**
 * Initializes Nosto cart update listeners
 * This function will wait for Nosto to be ready and register cart update listeners
 */ export const makeNostoCartIntegration = async ()=>{
    if (NOSTO_LOG) {
        console.log('Nosto: Starting cart integration process');
    }
    // Wait for Nosto API to be fully ready (up to 15 seconds)
    const api = await waitForNostoAPI(15000);
    if (!api) {
        console.warn('Nosto: Skipping recommendations update - API not available');
        return;
    }
    try {
        if (NOSTO_LOG) {
            console.log('Nosto: Updating recommendations for cart change');
        }
        // API is already initialized and verified, so we can use it directly
        api.listen('cartupdated', ()=>{
            if (NOSTO_LOG) {
                console.log('Nosto: Reloading recommendations');
            }
            api.loadRecommendations({
                placementId: 'minicart-placement'
            });
        });
        if (NOSTO_LOG) {
            console.log('Nosto: Cart update listener registered successfully');
        }
    } catch (error) {
        console.error('Nosto: Failed to register cart listener', error);
    }
};
/**
 * Creates a callback function for minicart initialization that integrates with Nosto
 * Specifically designed for product page minicart interactions
 * Should only be used when already confirmed to be on a product page
 * @returns A function that can be used as onCartInitialized callback
 */ export const makeNostoRecommendationsMinicart = ()=>{
    return async (_)=>{
        if (NOSTO_LOG) {
            console.log('Nosto: Minicart callback called - starting integration');
        }
        try {
            await makeNostoCartIntegration();
            if (NOSTO_LOG) {
                console.log('Nosto: Minicart integration completed');
            }
        } catch (error) {
            console.error('Nosto: Minicart integration failed', error);
        }
    };
};
